import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { getSupabase, isSupabaseConfigured } from '../utils/supabaseClient';

const STORAGE_KEY = 'restock-route-stores-v1';
const DEFAULT_CENTER = { lat: 33.7887, lng: -117.8755 };
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

/** Pikachu sprite for store pins (default Leaflet shadow was drawing as a pale box behind the blue pin). Anchor tuned for empty space above the character in the PNG. */
const STORE_MAP_MARKER_PX = 44;
const storeMapMarkerIcon = L.icon({
  iconUrl: `${process.env.PUBLIC_URL ?? ''}/images/pokemon-map-marker.png`,
  iconSize: [STORE_MAP_MARKER_PX, STORE_MAP_MARKER_PX],
  iconAnchor: [STORE_MAP_MARKER_PX / 2, Math.round(STORE_MAP_MARKER_PX * 0.92)],
  popupAnchor: [0, -Math.round(STORE_MAP_MARKER_PX * 0.85)],
});

const WEEKDAYS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

/** Retail format hints (examples only; pick one per store). */
const STORE_FORMAT_TAGS = [
  {
    value: 'big',
    label: 'Big',
    hint: 'e.g. Target, Walmart, Best Buy, Costco',
  },
  {
    value: 'small',
    label: 'Small',
    hint: 'e.g. CVS, Walgreens, Dollar Tree',
  },
  {
    value: 'vending_machine',
    label: 'Vending machine',
    hint: 'e.g. Albertsons, Ralphs, Pavilions',
  },
];

/** Green / blue / orange styling for Big, Small, and Vending machine. */
const STORE_TAG_COLORS = {
  big: {
    toggleOn: 'border-green-600 text-green-800',
    toggleOff: 'border-transparent text-green-800/85',
    badge: 'border-green-600 text-green-950 bg-green-100',
  },
  small: {
    toggleOn: 'border-blue-600 text-blue-800',
    toggleOff: 'border-transparent text-blue-800/85',
    badge: 'border-blue-600 text-blue-950 bg-blue-100',
  },
  vending_machine: {
    toggleOn: 'border-orange-600 text-orange-900',
    toggleOff: 'border-transparent text-orange-900/85',
    badge: 'border-orange-700 text-orange-950 bg-orange-100',
  },
};

const VISIT_NOTE_MAX = 6000;
const STORE_COLUMNS =
  'id,name,detail,map_url,address,lat,lng,shipment_days,visit_log,store_tag';

function normalizeStoreTag(raw) {
  if (typeof raw !== 'string') return null;
  const v = raw.trim().toLowerCase().replace(/\s+/g, '_');
  if (v === 'vendingmachine') return 'vending_machine';
  if (v === 'big' || v === 'small' || v === 'vending_machine') return v;
  return null;
}

/** Keywords in place names → store format tag (first match wins within each group order). */
const STORE_TAG_NAME_MATCHERS = [
  {
    tag: 'big',
    patterns: [
      /best\s*buy\b/i,
      /\bcostco\b/i,
      /\btarget\b/i,
      /\bwalmart\b/i,
      /\bwal\s*-?\s*mart\b/i,
    ],
  },
  {
    tag: 'small',
    patterns: [/\bdollar\s*tree\b/i, /\bwalgreens?\b/i, /\bcvs\b/i],
  },
  {
    tag: 'vending_machine',
    patterns: [/\bpavilions?\b/i, /\bralphs?\b/i, /\balbertsons?\b/i],
  },
];

function inferStoreTagFromName(name) {
  if (typeof name !== 'string') return null;
  const trimmed = name.trim();
  if (trimmed.length < 2) return null;
  for (const { tag, patterns } of STORE_TAG_NAME_MATCHERS) {
    for (const re of patterns) {
      if (re.test(trimmed)) return tag;
    }
  }
  return null;
}

function formatTagLabel(tag) {
  if (!tag) return '';
  const found = STORE_FORMAT_TAGS.find((t) => t.value === tag);
  return found?.label ?? tag;
}

function getStoreTagToggleClass(tag, isActive) {
  const c = STORE_TAG_COLORS[tag];
  const base = 'px-3 py-1 text-sm border-2 transition';
  if (!c) {
    return `${base} ${
      isActive ? 'border-current' : 'border-transparent opacity-65'
    }`;
  }
  return `${base} ${isActive ? c.toggleOn : `${c.toggleOff} opacity-80`}`;
}

/** Pill-style label for lists, map popup, inline next to store name */
function storeTagBadgeClassName(tag) {
  const c = STORE_TAG_COLORS[tag]?.badge;
  const base =
    'inline-flex items-center px-2 py-0.5 text-xs font-semibold border-2 rounded-sm whitespace-nowrap';
  return c ? `${base} ${c}` : base;
}

function genId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function formatVisitAt(iso) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** `datetime-local` input value in the user's local timezone */
function isoToDatetimeLocalValue(iso) {
  try {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
}

function datetimeLocalValueToIso(value) {
  if (!value || typeof value !== 'string') return null;
  const t = Date.parse(value);
  if (!Number.isFinite(t)) return null;
  return new Date(t).toISOString();
}

function normalizeVisitLog(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const v of raw) {
    if (!v || typeof v !== 'object') continue;
    const id = typeof v.id === 'string' && v.id ? v.id : genId();
    const at = typeof v.at === 'string' ? v.at : '';
    const t = Date.parse(at);
    if (!Number.isFinite(t)) continue;
    let note = typeof v.note === 'string' ? v.note : '';
    if (note.length > VISIT_NOTE_MAX) note = note.slice(0, VISIT_NOTE_MAX);
    out.push({ id, at: new Date(t).toISOString(), note });
  }
  out.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  return out;
}

function normalizeStore(raw) {
  const days = Array.isArray(raw.shipmentDays)
    ? [
        ...new Set(
          raw.shipmentDays.filter(
            (d) => Number.isInteger(d) && d >= 0 && d <= 6,
          ),
        ),
      ].sort((a, b) => a - b)
    : [];
  const visitRaw =
    raw.visitLog ??
    (Array.isArray(raw.visit_log) ? raw.visit_log : undefined);
  const rawTag =
    raw.storeTag ?? (typeof raw.store_tag === 'string' ? raw.store_tag : null);
  return {
    id: typeof raw.id === 'string' ? raw.id : genId(),
    name: typeof raw.name === 'string' ? raw.name : 'Untitled',
    detail: typeof raw.detail === 'string' ? raw.detail : '',
    mapUrl: typeof raw.mapUrl === 'string' ? raw.mapUrl : '',
    address: typeof raw.address === 'string' ? raw.address : '',
    lat: Number.isFinite(raw.lat) ? raw.lat : null,
    lng: Number.isFinite(raw.lng) ? raw.lng : null,
    shipmentDays: days,
    storeTag: normalizeStoreTag(rawTag),
    visitLog: normalizeVisitLog(visitRaw),
  };
}

function rowToStore(row) {
  const days = Array.isArray(row.shipment_days)
    ? row.shipment_days.filter(
        (d) => Number.isInteger(d) && d >= 0 && d <= 6,
      )
    : [];
  return normalizeStore({
    id: row.id,
    name: row.name,
    detail: row.detail ?? '',
    mapUrl: row.map_url ?? '',
    address: row.address ?? '',
    lat: Number.isFinite(row.lat) ? row.lat : null,
    lng: Number.isFinite(row.lng) ? row.lng : null,
    shipmentDays: days,
    store_tag: row.store_tag,
    visit_log: row.visit_log,
  });
}

async function geocodeAddress(address) {
  const q = address.trim();
  if (!q) return null;
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=us&q=${encodeURIComponent(
    q,
  )}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Address lookup failed');
  }
  const data = await response.json();
  if (!Array.isArray(data) || data.length === 0) return null;
  const first = data[0];
  const lat = Number(first.lat);
  const lng = Number(first.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

async function reverseGeocode(lat, lng) {
  const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`;
  const response = await fetch(url);
  if (!response.ok) return null;
  const data = await response.json();
  if (!data) return null;

  const addr = data.address ?? {};
  const house = addr.house_number ?? '';
  const road = addr.road ?? addr.pedestrian ?? addr.footway ?? '';
  const city =
    addr.city ?? addr.town ?? addr.village ?? addr.hamlet ?? addr.municipality ?? '';
  const state = addr.state_code ?? addr.state ?? '';
  const zip = addr.postcode ?? '';

  // Named venues (shops, amenities) often belong on mailing-address lines but aren't house_number.
  const namedVenue =
    addr.shop ??
    addr.amenity ??
    addr.office ??
    addr.tourism ??
    '';
  const line1 = [namedVenue, house, road].filter(Boolean).join(' ').trim();
  const line2 = [city, state].filter(Boolean).join(', ').trim();
  const line2WithZip = [line2, zip].filter(Boolean).join(' ').trim();
  const formatted = [line1, line2WithZip].filter(Boolean).join(', ').trim();

  return formatted || data.display_name || null;
}

function scoreGoogleReverseCandidate(r, placeNameHint = '') {
  const types = r.types ?? [];
  const lt = r.geometry?.location_type ?? '';
  let s = 0;
  if (lt === 'ROOFTOP') s += 100;
  else if (lt === 'RANGE_INTERPOLATED') s += 75;
  else if (lt === 'GEOMETRIC_CENTER') s += 40;
  else if (lt === 'APPROXIMATE') s += 20;

  if (types.includes('street_address')) s += 60;
  if (types.includes('premise')) s += 50;
  if (types.includes('subpremise')) s += 35;
  if (types.includes('establishment')) s += 45;
  if (types.includes('point_of_interest')) s += 40;

  // Reverse geocode can return a bare nearby road; avoid choosing that over a storefront address.
  const routeOnly =
    types.includes('route') &&
    !types.includes('street_address') &&
    !types.includes('premise') &&
    !types.includes('establishment') &&
    !types.includes('point_of_interest');
  if (routeOnly) s -= 90;

  const hint = placeNameHint.trim().toLowerCase();
  if (hint.length >= 3) {
    const fa = (r.formatted_address ?? '').toLowerCase();
    const words = hint
      .split(/[\s,+]+/)
      .map((w) => w.trim())
      .filter((w) => w.length >= 3);
    for (const w of words.slice(0, 3)) {
      if (fa.includes(w)) {
        s += 25;
        break;
      }
    }
  }

  return s;
}

async function reverseGeocodeGoogle(lat, lng, placeNameHint = '') {
  if (!GOOGLE_MAPS_API_KEY) return null;
  const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`;
  const response = await fetch(url);
  if (!response.ok) return null;
  const data = await response.json();
  if (!data || data.status !== 'OK' || !Array.isArray(data.results)) return null;
  const results = data.results;
  if (results.length === 0) return null;
  let best = results[0];
  let bestScore = scoreGoogleReverseCandidate(best, placeNameHint);
  for (let i = 1; i < results.length; i += 1) {
    const r = results[i];
    const sc = scoreGoogleReverseCandidate(r, placeNameHint);
    if (sc > bestScore) {
      best = r;
      bestScore = sc;
    }
  }
  return best?.formatted_address ?? null;
}

async function bestAddressFromCoords(lat, lng, placeNameHint = '') {
  // Prefer Google Geocoding (better match quality for Google Maps place URLs),
  // then fall back to OpenStreetMap reverse geocoding.
  const google = await reverseGeocodeGoogle(lat, lng, placeNameHint);
  if (google) return google;
  return reverseGeocode(lat, lng);
}

const MAPS_EXPAND_ORIGIN = (process.env.REACT_APP_MAPS_EXPAND_URL || '').replace(
  /\/$/,
  '',
);

/**
 * maps.app.goo.gl / goo.gl links redirect; the browser cannot read that redirect in fetch (CORS).
 * Netlify `/api/expand-maps` unwraps them server-side. Locally: `netlify dev` or set REACT_APP_MAPS_EXPAND_URL.
 */
function parseJsonBody(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

async function expandMapsShortUrlIfNeeded(raw) {
  const trimmed = raw.trim();
  let host;
  try {
    host = new URL(trimmed).hostname.toLowerCase();
  } catch {
    return trimmed;
  }

  const isShort =
    host === 'goo.gl' ||
    host === 'www.goo.gl' ||
    host === 'maps.app.goo.gl';
  if (!isShort) return trimmed;

  const qs = `/api/expand-maps?url=${encodeURIComponent(trimmed)}`;
  const endpoint = MAPS_EXPAND_ORIGIN ? `${MAPS_EXPAND_ORIGIN}${qs}` : qs;

  const res = await fetch(endpoint);
  const bodyText = await res.text();
  const data = parseJsonBody(bodyText);

  const looksLikeSpaHtml =
    bodyText.trimStart().toLowerCase().startsWith('<!doctype') ||
    bodyText.trimStart().toLowerCase().startsWith('<html');

  if (looksLikeSpaHtml || !data) {
    throw new Error(
      'Short Maps links need a server to follow redirects. Use a full https://www.google.com/maps/... URL, ' +
        'run `netlify dev` locally, or set REACT_APP_MAPS_EXPAND_URL to a Netlify deployment that serves /api/expand-maps ' +
        '(GitHub Pages and `npm start` alone do not provide that API).',
    );
  }

  if (!res.ok) {
    const detail = data?.error ? ` (${data.error})` : '';
    throw new Error(
      `Short link could not be expanded${detail}. Use the full google.com/maps link, run netlify dev, or set REACT_APP_MAPS_EXPAND_URL.`,
    );
  }

  if (!data?.expandedUrl || typeof data.expandedUrl !== 'string') {
    throw new Error('Short link expansion returned no URL.');
  }
  return data.expandedUrl;
}

function parseGoogleMapsLink(urlString) {
  try {
    const parsed = new URL(urlString.trim());
    if (!parsed.hostname.includes('google.')) {
      return { error: 'Must be a Google Maps URL.' };
    }

    const result = { name: '', address: '', lat: null, lng: null };
    const placeMatch = parsed.pathname.match(/\/place\/([^/]+)/i);
    if (placeMatch?.[1]) {
      result.name = decodeURIComponent(placeMatch[1].replace(/\+/g, ' '));
    }

    // Prefer explicit place coordinates embedded in `data=` over viewport center.
    const decodedPath = decodeURIComponent(parsed.pathname);
    const dataCoordsMatch = decodedPath.match(/!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/);
    if (dataCoordsMatch?.[1] && dataCoordsMatch?.[2]) {
      const lat = Number(dataCoordsMatch[1]);
      const lng = Number(dataCoordsMatch[2]);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        result.lat = lat;
        result.lng = lng;
      }
    } else {
      const atMatch = parsed.pathname.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
      if (atMatch?.[1] && atMatch?.[2]) {
        const lat = Number(atMatch[1]);
        const lng = Number(atMatch[2]);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          result.lat = lat;
          result.lng = lng;
        }
      }
    }

    const q = parsed.searchParams.get('q') || parsed.searchParams.get('query');
    if (q) {
      const trimmed = q.trim();
      const coordMatch = trimmed.match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/);
      if (coordMatch) {
        const lat = Number(coordMatch[1]);
        const lng = Number(coordMatch[2]);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          result.lat = lat;
          result.lng = lng;
        }
      } else {
        result.address = trimmed;
      }
    }

    if (!result.name && result.address) {
      const parts = result.address.split(',');
      result.name = parts[0]?.trim() ?? '';
    }

    return result;
  } catch {
    return { error: 'Invalid URL.' };
  }
}

function loadStores() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeStore);
  } catch {
    return [];
  }
}

function saveStores(stores) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stores));
  } catch {
    /* ignore quota / private mode */
  }
}

function sortByName(a, b) {
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}

function getToggleClass(isActive) {
  return `px-3 py-1 text-sm border-2 transition ${
    isActive ? 'border-current' : 'border-transparent opacity-65'
  }`;
}

export function RestockRoute() {
  const storeFormSectionRef = useRef(null);
  const allowInferStoreTagFromNameRef = useRef(true);
  const markStoreTagChosenByUser = () => {
    allowInferStoreTagFromNameRef.current = false;
  };

  const [stores, setStores] = useState([]);
  const [ready, setReady] = useState(false);
  const [backend, setBackend] = useState(null);
  const [cloudUserId, setCloudUserId] = useState(null);
  const [initError, setInitError] = useState(null);

  const [name, setName] = useState('');
  const [detail, setDetail] = useState('');
  const [mapUrl, setMapUrl] = useState('');
  const [address, setAddress] = useState('');
  const [shipmentDays, setShipmentDays] = useState([]);
  const [formatTag, setFormatTag] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [geocodeStatus, setGeocodeStatus] = useState('');
  const [mapImportStatus, setMapImportStatus] = useState('');
  const [storeNameFilters, setStoreNameFilters] = useState([]);
  const [dayFilter, setDayFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [visitLoggingForId, setVisitLoggingForId] = useState(null);
  const [visitDraft, setVisitDraft] = useState('');
  const [editingVisit, setEditingVisit] = useState(null);
  const [editVisitNote, setEditVisitNote] = useState('');
  const [editVisitAtLocal, setEditVisitAtLocal] = useState('');

  const today = new Date().getDay();

  useEffect(() => {
    if (backend === 'local') {
      saveStores(stores);
    }
  }, [stores, backend]);

  useEffect(() => {
    let cancelled = false;

    async function initCloud(supabase) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      let activeSession = session;

      if (!activeSession) {
        const { data, error } = await supabase.auth.signInAnonymously();
        if (error || !data.session) {
          throw new Error(error?.message ?? 'Anonymous sign-in failed');
        }
        activeSession = data.session;
      }

      const userId = activeSession.user.id;

      const { data: rows, error: fetchErr } = await supabase
        .from('restock_route_stores')
        .select(STORE_COLUMNS)
        .order('name');

      if (fetchErr) throw fetchErr;

      let mapped = (rows ?? []).map(rowToStore);

      if (mapped.length === 0) {
        const localOnly = loadStores();
        if (localOnly.length > 0) {
          const inserts = localOnly.map((s) => ({
            user_id: userId,
            name: s.name,
            detail: s.detail,
            map_url: s.mapUrl ?? '',
            address: s.address ?? '',
            lat: s.lat,
            lng: s.lng,
            shipment_days: s.shipmentDays,
            store_tag: s.storeTag ?? null,
            visit_log: normalizeVisitLog(s.visitLog ?? []),
          }));
          const { data: inserted, error: insErr } = await supabase
            .from('restock_route_stores')
            .insert(inserts)
            .select(STORE_COLUMNS);
          if (!insErr && inserted?.length) {
            mapped = inserted.map(rowToStore);
            try {
              localStorage.removeItem(STORAGE_KEY);
            } catch {
              /* ignore */
            }
          }
        }
      }

      if (!cancelled) {
        setCloudUserId(userId);
        setStores(mapped);
        setBackend('cloud');
        setReady(true);
      }
    }

    async function init() {
      if (!isSupabaseConfigured()) {
        if (!cancelled) {
          setStores(loadStores());
          setBackend('local');
          setReady(true);
        }
        return;
      }

      const supabase = getSupabase();
      try {
        await initCloud(supabase);
      } catch (err) {
        if (!cancelled) {
          setInitError(err.message ?? String(err));
          setStores(loadStores());
          setBackend('local');
          setReady(true);
        }
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, []);

  const storesToday = useMemo(() => {
    const base = stores.filter((s) => s.shipmentDays.includes(today));
    return base.sort(sortByName);
  }, [stores, today]);

  const uniqueStoreNames = useMemo(() => {
    const set = new Set(stores.map((s) => s.name).filter(Boolean));
    return [...set].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );
  }, [stores]);

  const filteredStores = useMemo(() => {
    return stores
      .filter((s) => {
        const nameOk =
          storeNameFilters.length === 0 || storeNameFilters.includes(s.name);
        const dayOk =
          dayFilter === 'all' || s.shipmentDays.includes(Number(dayFilter));
        const tagOk =
          tagFilter === 'all' ||
          (tagFilter === 'untagged' ? !s.storeTag : s.storeTag === tagFilter);
        return nameOk && dayOk && tagOk;
      })
      .sort(sortByName);
  }, [stores, storeNameFilters, dayFilter, tagFilter]);

  const mappableStores = useMemo(() => {
    return filteredStores.filter(
      (s) => Number.isFinite(s.lat) && Number.isFinite(s.lng),
    );
  }, [filteredStores]);

  const byWeekday = useMemo(() => {
    const map = WEEKDAYS.reduce((acc, { value }) => {
      acc[value] = [];
      return acc;
    }, {});
    stores.forEach((s) => {
      s.shipmentDays.forEach((d) => {
        if (map[d]) map[d].push(s.name);
      });
    });
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    });
    return map;
  }, [stores]);

  const longDate = new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(new Date());

  const resetForm = () => {
    setName('');
    setDetail('');
    setMapUrl('');
    setAddress('');
    setShipmentDays([]);
    setFormatTag(null);
    setEditingId(null);
    setGeocodeStatus('');
    setMapImportStatus('');
    allowInferStoreTagFromNameRef.current = true;
  };

  const toggleShipmentDay = (day) => {
    setShipmentDays((prev) =>
      prev.includes(day)
        ? prev.filter((d) => d !== day)
        : [...prev, day].sort((a, b) => a - b),
    );
  };

  const toggleStoreNameFilter = (storeName) => {
    setStoreNameFilters((prev) =>
      prev.includes(storeName)
        ? prev.filter((nameValue) => nameValue !== storeName)
        : [...prev, storeName],
    );
  };

  const clearFilters = () => {
    setStoreNameFilters([]);
    setDayFilter('all');
    setTagFilter('all');
  };

  const openVisitLog = (storeId) => {
    setEditingVisit(null);
    setEditVisitNote('');
    setEditVisitAtLocal('');
    setVisitLoggingForId(storeId);
    setVisitDraft('');
  };

  const cancelVisitLog = () => {
    setVisitLoggingForId(null);
    setVisitDraft('');
  };

  const cancelEditVisit = () => {
    setEditingVisit(null);
    setEditVisitNote('');
    setEditVisitAtLocal('');
  };

  const openEditVisit = (storeId, entry) => {
    cancelVisitLog();
    setEditingVisit({ storeId, entryId: entry.id });
    setEditVisitNote(entry.note);
    setEditVisitAtLocal(isoToDatetimeLocalValue(entry.at));
  };

  const saveEditVisit = async (storeId) => {
    if (!editingVisit || editingVisit.storeId !== storeId) return;
    const { entryId } = editingVisit;
    const trimmed = editVisitNote.trim();
    if (!trimmed) {
      window.alert('Add a note for this visit.');
      return;
    }
    if (!ready) return;
    const atIso = datetimeLocalValueToIso(editVisitAtLocal);
    if (!atIso) {
      window.alert('Choose a valid visit date and time.');
      return;
    }

    const store = stores.find((s) => s.id === storeId);
    if (!store) return;

    const nextVisitLog = normalizeVisitLog(
      store.visitLog.map((v) =>
        v.id === entryId
          ? { id: v.id, at: atIso, note: trimmed.slice(0, VISIT_NOTE_MAX) }
          : v,
      ),
    );

    const apply = (prev) =>
      prev.map((s) =>
        s.id === storeId ? normalizeStore({ ...s, visitLog: nextVisitLog }) : s,
      );

    if (backend === 'local') {
      setStores(apply);
      cancelEditVisit();
      return;
    }

    const supabase = getSupabase();
    if (!supabase) return;

    const { error } = await supabase
      .from('restock_route_stores')
      .update({ visit_log: nextVisitLog })
      .eq('id', storeId);

    if (error) {
      window.alert(error.message);
      return;
    }
    setStores(apply);
    cancelEditVisit();
  };

  const submitVisitLog = async (storeId) => {
    const trimmed = visitDraft.trim();
    if (!trimmed) {
      window.alert('Add a note for this visit.');
      return;
    }
    if (!ready) return;

    const store = stores.find((s) => s.id === storeId);
    if (!store) return;

    cancelEditVisit();

    const entry = {
      id: genId(),
      at: new Date().toISOString(),
      note: trimmed.slice(0, VISIT_NOTE_MAX),
    };

    const nextVisitLog = normalizeVisitLog([
      entry,
      ...(store.visitLog ?? []),
    ]);

    const apply = (prev) =>
      prev.map((s) =>
        s.id === storeId ? normalizeStore({ ...s, visitLog: nextVisitLog }) : s,
      );

    if (backend === 'local') {
      setStores(apply);
      cancelVisitLog();
      return;
    }

    const supabase = getSupabase();
    if (!supabase) return;

    const { error } = await supabase
      .from('restock_route_stores')
      .update({ visit_log: nextVisitLog })
      .eq('id', storeId);

    if (error) {
      window.alert(error.message);
      return;
    }
    setStores(apply);
    cancelVisitLog();
  };

  const handleGeocode = async () => {
    if (!address.trim()) {
      setGeocodeStatus('Add an address first.');
      return;
    }
    setGeocodeStatus('Looking up address...');
    try {
      const coords = await geocodeAddress(address);
      if (!coords) {
        setGeocodeStatus('No match found. Try a fuller address.');
        return;
      }
      setGeocodeStatus(
        `Location set (${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}).`,
      );
    } catch (err) {
      setGeocodeStatus(err.message ?? 'Address lookup failed.');
    }
  };

  const handleImportMapLink = async () => {
    if (!mapUrl.trim()) {
      setMapImportStatus('Paste a Google Maps link first.');
      return;
    }
    let linkToParse = mapUrl.trim();
    try {
      linkToParse = await expandMapsShortUrlIfNeeded(mapUrl);
    } catch (err) {
      setMapImportStatus(err.message ?? 'Could not expand link.');
      return;
    }

    const parsed = parseGoogleMapsLink(linkToParse);
    if (parsed.error) {
      setMapImportStatus(parsed.error);
      return;
    }

    const mapsNameTrimmed =
      typeof parsed.name === 'string' ? parsed.name.trim() : '';
    const nameForInfer = mapsNameTrimmed || name.trim();

    if (parsed.name) setName(parsed.name);
    if (parsed.address) setAddress(parsed.address);

    try {
      if (
        !parsed.address &&
        Number.isFinite(parsed.lat) &&
        Number.isFinite(parsed.lng)
      ) {
        const reverse = await bestAddressFromCoords(
          parsed.lat,
          parsed.lng,
          parsed.name,
        );
        if (reverse) setAddress(reverse);
      }
      setMapImportStatus('Imported details from Google Maps link.');
    } catch {
      setMapImportStatus('Imported partially. Address lookup failed.');
    }

    if (nameForInfer) {
      const inferred = inferStoreTagFromName(nameForInfer);
      if (inferred) setFormatTag(inferred);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ready) return;

    let importedName = '';
    let importedAddress = '';
    let importedLat = null;
    let importedLng = null;
    const cleanedMapUrl = mapUrl.trim();

    let linkToParseForImport = cleanedMapUrl;
    if (cleanedMapUrl) {
      try {
        linkToParseForImport = await expandMapsShortUrlIfNeeded(cleanedMapUrl);
      } catch (err) {
        window.alert(err.message ?? 'Could not expand Maps link.');
        return;
      }
      const parsed = parseGoogleMapsLink(linkToParseForImport);
      if (parsed.error) {
        window.alert(parsed.error);
        return;
      }
      importedName = parsed.name ?? '';
      importedAddress = parsed.address ?? '';
      importedLat = Number.isFinite(parsed.lat) ? parsed.lat : null;
      importedLng = Number.isFinite(parsed.lng) ? parsed.lng : null;
    }

    const trimmedName = (name.trim() || importedName).trim();
    if (!trimmedName) return;

    const inferredFromName = inferStoreTagFromName(trimmedName);
    const resolvedStoreTag =
      normalizeStoreTag(formatTag) ??
      (allowInferStoreTagFromNameRef.current ? inferredFromName : null);

    const payload = {
      name: trimmedName,
      detail: detail.trim(),
      mapUrl: cleanedMapUrl,
      address: (address.trim() || importedAddress).trim(),
      shipmentDays: [...shipmentDays],
      storeTag: resolvedStoreTag,
      lat: importedLat,
      lng: importedLng,
    };

    if (
      !payload.address &&
      Number.isFinite(payload.lat) &&
      Number.isFinite(payload.lng)
    ) {
      try {
        const reverse = await bestAddressFromCoords(
          payload.lat,
          payload.lng,
          trimmedName,
        );
        if (reverse) payload.address = reverse;
      } catch {
        // Keep saving when reverse geocode fails.
      }
    }

    if (payload.address && (!Number.isFinite(payload.lat) || !Number.isFinite(payload.lng))) {
      try {
        const coords = await geocodeAddress(payload.address);
        if (coords) {
          payload.lat = coords.lat;
          payload.lng = coords.lng;
        }
      } catch {
        // Keep saving data even if geocoding fails.
      }
    }

    if (backend === 'local') {
      if (editingId) {
        setStores((prev) =>
          prev.map((s) =>
            s.id === editingId ? normalizeStore({ ...s, ...payload }) : s,
          ),
        );
      } else {
        setStores((prev) => [
          ...prev,
          normalizeStore({ id: genId(), ...payload }),
        ]);
      }
      resetForm();
      return;
    }

    const supabase = getSupabase();
    if (!supabase || !cloudUserId) return;

    if (editingId) {
      const { error } = await supabase
        .from('restock_route_stores')
        .update({
          name: payload.name,
          detail: payload.detail,
          map_url: payload.mapUrl,
          address: payload.address,
          lat: payload.lat,
          lng: payload.lng,
          shipment_days: payload.shipmentDays,
          store_tag: payload.storeTag,
        })
        .eq('id', editingId);
      if (error) {
        window.alert(error.message);
        return;
      }
      setStores((prev) =>
        prev.map((s) =>
          s.id === editingId ? normalizeStore({ ...s, ...payload }) : s,
        ),
      );
    } else {
      const { data, error } = await supabase
        .from('restock_route_stores')
        .insert({
          user_id: cloudUserId,
          name: payload.name,
          detail: payload.detail,
          map_url: payload.mapUrl,
          address: payload.address,
          lat: payload.lat,
          lng: payload.lng,
          shipment_days: payload.shipmentDays,
          store_tag: payload.storeTag,
          visit_log: [],
        })
        .select(STORE_COLUMNS)
        .single();
      if (error) {
        window.alert(error.message);
        return;
      }
      setStores((prev) => [...prev, rowToStore(data)]);
    }
    resetForm();
  };

  const startEdit = (store) => {
    cancelVisitLog();
    cancelEditVisit();
    allowInferStoreTagFromNameRef.current = true;
    setEditingId(store.id);
    setName(store.name);
    setDetail(store.detail);
    setMapUrl(store.mapUrl ?? '');
    setAddress(store.address ?? '');
    setShipmentDays([...store.shipmentDays]);
    setFormatTag(store.storeTag ?? null);
    queueMicrotask(() => {
      storeFormSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  const removeStore = async (id, label) => {
    if (!window.confirm(`Remove "${label}"?`)) return;

    if (backend === 'local') {
      setStores((prev) => prev.filter((s) => s.id !== id));
      if (editingId === id) resetForm();
      if (visitLoggingForId === id) cancelVisitLog();
      if (editingVisit?.storeId === id) cancelEditVisit();
      return;
    }

    const supabase = getSupabase();
    if (!supabase) return;

    const { error } = await supabase.from('restock_route_stores').delete().eq('id', id);
    if (error) {
      window.alert(error.message);
      return;
    }
    setStores((prev) => prev.filter((s) => s.id !== id));
    if (editingId === id) resetForm();
    if (visitLoggingForId === id) cancelVisitLog();
    if (editingVisit?.storeId === id) cancelEditVisit();
  };

  const storageBlurb =
    backend === 'cloud'
      ? 'Synced with Supabase (anonymous account on this browser).'
      : backend === 'local'
        ? 'Stored only in this browser (local storage).'
        : '';

  if (!ready) {
    return (
      <main className='flex-grow px-4 pb-12 pt-8 max-w-5xl mx-auto w-full'>
        <p className='text-sm'>Loading shipment tracker…</p>
      </main>
    );
  }

  return (
    <main className='flex-grow px-4 pb-12 pt-2 max-w-5xl mx-auto w-full'>
      <header className='mb-8'>
        <p className='text-sm opacity-80 mb-1'>Private bookmark — not linked on the site</p>
        <h1 className='text-3xl sm:text-4xl mb-2'>Shipment days</h1>
        <p className='text-sm sm:text-base max-w-2xl opacity-90'>
          Track when stores usually get deliveries so you know where to check after restocks.
        </p>
        <p className='text-sm mt-2 max-w-2xl opacity-85'>
          <span className='border-b border-current'>{storageBlurb}</span>
          {initError ? (
            <span className='block mt-2 opacity-90'>
              Cloud sync unavailable ({initError}). Using local storage instead.
            </span>
          ) : null}
        </p>
      </header>

      <section className='mb-10' aria-labelledby='filters-heading'>
        <h2 id='filters-heading' className='text-xl mb-3'>
          Filters
        </h2>
        <div className='border-2 border-current p-4 sm:p-5 space-y-4'>
          <div>
            <p className='text-sm mb-2'>Store name</p>
            {uniqueStoreNames.length === 0 ? (
              <p className='text-sm opacity-70'>No stores yet.</p>
            ) : (
              <div className='flex flex-wrap gap-2'>
                {uniqueStoreNames.map((storeName) => {
                  const active = storeNameFilters.includes(storeName);
                  return (
                    <button
                      key={storeName}
                      type='button'
                      onClick={() => toggleStoreNameFilter(storeName)}
                      className={getToggleClass(active)}
                    >
                      {storeName}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <p className='text-sm mb-2'>Store tag</p>
            <div className='flex flex-wrap gap-2'>
              <button
                type='button'
                onClick={() => setTagFilter('all')}
                className={getToggleClass(tagFilter === 'all')}
              >
                All
              </button>
              <button
                type='button'
                onClick={() => setTagFilter('untagged')}
                className={getToggleClass(tagFilter === 'untagged')}
              >
                Untagged
              </button>
              {STORE_FORMAT_TAGS.map((t) => {
                const active = tagFilter === t.value;
                return (
                  <button
                    key={t.value}
                    type='button'
                    onClick={() => setTagFilter(t.value)}
                    className={getStoreTagToggleClass(t.value, active)}
                    title={t.hint}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <p className='text-sm mb-2'>Shipment day</p>
            <div className='flex flex-wrap gap-2'>
              <button
                type='button'
                onClick={() => setDayFilter('all')}
                className={getToggleClass(dayFilter === 'all')}
              >
                All
              </button>
              {WEEKDAYS.map((day) => {
                const active = String(day.value) === dayFilter;
                return (
                  <button
                    key={day.value}
                    type='button'
                    onClick={() => setDayFilter(String(day.value))}
                    className={getToggleClass(active)}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className='flex items-center justify-between gap-2'>
            <p className='text-sm opacity-80'>
              Showing {filteredStores.length} of {stores.length} stores
            </p>
            <button
              type='button'
              onClick={clearFilters}
              className='px-3 py-1 text-sm border-2 border-current opacity-85'
            >
              Clear filters
            </button>
          </div>
        </div>
      </section>

      <section className='mb-10' aria-labelledby='map-heading'>
        <h2 id='map-heading' className='text-xl mb-3'>
          Store map (centered on 92868)
        </h2>
        <div className='border-2 border-current p-2'>
          <MapContainer
            center={[DEFAULT_CENTER.lat, DEFAULT_CENTER.lng]}
            zoom={12}
            className='h-[360px] w-full'
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
            />
            {mappableStores.map((store) => (
              <Marker key={store.id} position={[store.lat, store.lng]} icon={storeMapMarkerIcon}>
                <Popup>
                  <strong>{store.name}</strong>
                  {store.storeTag ? (
                    <>
                      <br />
                      <span className={storeTagBadgeClassName(store.storeTag)}>
                        {formatTagLabel(store.storeTag)}
                      </span>
                    </>
                  ) : null}
                  <br />
                  {store.address || 'No address'}
                  <br />
                  Days:{' '}
                  {store.shipmentDays.length
                    ? store.shipmentDays
                        .map((d) => WEEKDAYS.find((w) => w.value === d)?.label)
                        .join(', ')
                    : 'not set'}
                  {store.visitLog?.[0] ? (
                    <>
                      <br />
                      Last visit: {formatVisitAt(store.visitLog[0].at)}
                      <br />
                      <span style={{ whiteSpace: 'pre-wrap' }}>
                        {store.visitLog[0].note}
                      </span>
                    </>
                  ) : null}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
        <p className='text-sm mt-2 opacity-80'>
          {mappableStores.length} stores currently mapped. Save with an address
          or a Google Maps link to place a marker.
        </p>
      </section>

      <section
        className={`mb-10 p-4 sm:p-6 border-custom rounded-none ${
          storesToday.length ? '' : 'opacity-95'
        }`}
        aria-labelledby='today-heading'
      >
        <h2 id='today-heading' className='text-xl mb-2'>
          Today — {longDate}
        </h2>
        {storesToday.length === 0 ? (
          <p className='text-sm'>
            No stores marked for{' '}
            {WEEKDAYS.find((w) => w.value === today)?.label}.
            Add shipment weekdays below.
          </p>
        ) : (
          <ul className='space-y-2'>
            {storesToday.map((s) => {
              const latest = s.visitLog?.[0];
              return (
                <li key={s.id} className='space-y-1'>
                  <div className='flex flex-col sm:flex-row sm:items-baseline sm:gap-3'>
                    <span className='font-semibold'>{s.name}</span>
                    {s.storeTag ? (
                      <span className={storeTagBadgeClassName(s.storeTag)}>
                        {formatTagLabel(s.storeTag)}
                      </span>
                    ) : null}
                    {s.detail ? (
                      <span className='text-sm opacity-90 whitespace-pre-wrap'>
                        {s.detail}
                      </span>
                    ) : null}
                  </div>
                  {latest ? (
                    <p className='text-sm opacity-85'>
                      <span className='opacity-75'>Last visit </span>
                      {formatVisitAt(latest.at)}:{' '}
                      <span className='whitespace-pre-wrap'>{latest.note}</span>
                    </p>
                  ) : (
                    <p className='text-sm opacity-65'>No visits logged yet.</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className='mb-10' aria-labelledby='week-heading'>
        <h2 id='week-heading' className='text-xl mb-3'>
          Week map
        </h2>
        <div className='flex gap-2 overflow-x-auto pb-2 -mx-1 px-1'>
          {WEEKDAYS.map(({ value, label }) => {
            const names = byWeekday[value] || [];
            const isToday = value === today;
            return (
              <div
                key={value}
                className={`min-w-[7.5rem] flex-shrink-0 border-2 p-2 sm:p-3 ${
                  isToday ? 'border-custom' : 'border-current opacity-90'
                }`}
              >
                <div className='text-xs uppercase tracking-wide mb-2'>
                  {label}
                  {isToday ? ' · today' : ''}
                </div>
                {names.length === 0 ? (
                  <p className='text-xs opacity-70'>—</p>
                ) : (
                  <ul className='text-sm space-y-1'>
                    {names.map((n) => (
                      <li key={`${value}-${n}`}>{n}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section
        ref={storeFormSectionRef}
        className='mb-10 scroll-mt-6'
        aria-labelledby='form-heading'
      >
        <h2 id='form-heading' className='text-xl mb-3'>
          {editingId ? 'Edit store' : 'Add store'}
        </h2>
        <form
          onSubmit={handleSubmit}
          className='space-y-4 border-2 border-current p-4 sm:p-5 max-w-xl'
        >
          <div>
            <label htmlFor='store-name' className='block text-sm mb-1'>
              Store name
            </label>
            <input
              id='store-name'
              type='text'
              value={name}
              onChange={(e) => setName(e.target.value)}
              className='w-full px-3 py-2 border-2 border-current bg-transparent placeholder:opacity-50'
              placeholder='e.g. Target — Metreon'
              autoComplete='off'
            />
          </div>
          <div>
            <label htmlFor='store-address' className='block text-sm mb-1'>
              Address (optional if link import fills it)
            </label>
            <div className='mb-2'>
              <label htmlFor='store-map-url' className='block text-sm mb-1'>
                Google Maps link (optional import)
              </label>
              <input
                id='store-map-url'
                type='url'
                value={mapUrl}
                onChange={(e) => setMapUrl(e.target.value)}
                className='w-full px-3 py-2 border-2 border-current bg-transparent placeholder:opacity-50'
                placeholder='https://www.google.com/maps/place/… or https://maps.app.goo.gl/…'
                autoComplete='off'
              />
              <div className='mt-2 flex items-center gap-3'>
                <button
                  type='button'
                  onClick={handleImportMapLink}
                  className='px-3 py-1 text-sm border-2 border-current opacity-90'
                >
                  Import from link
                </button>
                {mapImportStatus ? (
                  <span className='text-xs opacity-80'>{mapImportStatus}</span>
                ) : null}
              </div>
            </div>
            <input
              id='store-address'
              type='text'
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className='w-full px-3 py-2 border-2 border-current bg-transparent placeholder:opacity-50'
              placeholder='e.g. 100 N Tustin St, Orange, CA 92867'
              autoComplete='off'
            />
            <div className='mt-2 flex items-center gap-3'>
              <button
                type='button'
                onClick={handleGeocode}
                className='px-3 py-1 text-sm border-2 border-current opacity-90'
              >
                Test address lookup
              </button>
              {geocodeStatus ? (
                <span className='text-xs opacity-80'>{geocodeStatus}</span>
              ) : null}
            </div>
          </div>
          <div>
            <label htmlFor='store-detail' className='block text-sm mb-1'>
              Notes (optional)
            </label>
            <textarea
              id='store-detail'
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              rows={3}
              className='w-full px-3 py-2 border-2 border-current bg-transparent placeholder:opacity-50 resize-y min-h-[4rem]'
              placeholder='Door times, usual truck day quote from staff, set reminders…'
            />
          </div>
          <fieldset className='border-0 p-0 m-0'>
            <legend className='text-sm mb-2'>Store tag (optional)</legend>
            <p className='text-xs opacity-75 mb-2 max-w-lg'>
              Examples are hints only. Import from link guesses a tag when the Maps place name matches
              a known chain (import again anytime to refresh from the latest name). If you tap None or
              pick a tag, saving without importing again keeps that choice and skips a name-only guess.
            </p>
            <div className='flex flex-wrap gap-2 mb-2'>
              <button
                type='button'
                onClick={() => {
                  markStoreTagChosenByUser();
                  setFormatTag(null);
                }}
                className={getToggleClass(formatTag === null)}
              >
                None
              </button>
              {STORE_FORMAT_TAGS.map((t) => {
                const on = formatTag === t.value;
                return (
                  <button
                    key={t.value}
                    type='button'
                    onClick={() => {
                      markStoreTagChosenByUser();
                      setFormatTag(t.value);
                    }}
                    className={getStoreTagToggleClass(t.value, on)}
                    title={t.hint}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            {formatTag ? (
              <p
                className={`text-xs ${
                  STORE_TAG_COLORS[formatTag]?.toggleOff ?? 'opacity-70'
                }`}
              >
                {STORE_FORMAT_TAGS.find((t) => t.value === formatTag)?.hint}
              </p>
            ) : null}
          </fieldset>
          <fieldset className='border-0 p-0 m-0'>
            <legend className='text-sm mb-2'>Shipment usually arrives</legend>
            <div className='flex flex-wrap gap-2'>
              {WEEKDAYS.map(({ value, label }) => {
                const on = shipmentDays.includes(value);
                return (
                  <button
                    key={value}
                    type='button'
                    onClick={() => toggleShipmentDay(value)}
                    className={getToggleClass(on)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </fieldset>
          <div className='flex flex-wrap gap-3 pt-2'>
            <button
              type='submit'
              className='px-4 py-2 border-2 border-current text-sm'
            >
              {editingId ? 'Save changes' : 'Add store'}
            </button>
            {editingId ? (
              <button
                type='button'
                onClick={resetForm}
                className='px-4 py-2 border-2 border-current text-sm opacity-90'
              >
                Cancel edit
              </button>
            ) : null}
          </div>
        </form>
      </section>

      <section aria-labelledby='list-heading'>
        <h2 id='list-heading' className='text-xl mb-3'>
          All stores ({filteredStores.length})
        </h2>
        {filteredStores.length === 0 ? (
          <p className='text-sm opacity-80'>No stores yet.</p>
        ) : (
          <ul className='space-y-4'>
            {filteredStores.map((s) => (
              <li
                key={s.id}
                className='border-2 border-current p-4 flex flex-col sm:flex-row sm:justify-between gap-3'
              >
                <div>
                  <div className='font-semibold text-lg'>
                    {s.name}
                    {s.storeTag ? (
                      <span className={`ml-2 ${storeTagBadgeClassName(s.storeTag)}`}>
                        {formatTagLabel(s.storeTag)}
                      </span>
                    ) : null}
                  </div>
                  <div className='text-sm mt-1'>
                    <span className='opacity-80'>Days: </span>
                    {s.shipmentDays.length === 0 ? (
                      <span className='opacity-70'>not set</span>
                    ) : (
                      s.shipmentDays
                        .map((d) => WEEKDAYS.find((w) => w.value === d)?.label)
                        .join(', ')
                    )}
                  </div>
                  {s.detail ? (
                    <p className='text-sm mt-2 whitespace-pre-wrap opacity-90'>
                      {s.detail}
                    </p>
                  ) : null}
                  {s.address ? (
                    <p className='text-sm mt-2 opacity-90'>
                      <span className='opacity-75'>Address: </span>
                      {s.address}
                    </p>
                  ) : null}
                  {s.mapUrl ? (
                    <p className='text-sm mt-2 opacity-90'>
                      <a
                        href={s.mapUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='underline'
                      >
                        Open in Google Maps
                      </a>
                    </p>
                  ) : null}

                  <div className='mt-4 pt-3 border-2 border-current border-opacity-[0.15] px-3 py-3 -mx-1'>
                    <p className='text-xs uppercase tracking-wide opacity-80 mb-2'>
                      Visits
                    </p>
                    {visitLoggingForId === s.id ? (
                      <div className='space-y-2'>
                        <label htmlFor={`visit-note-${s.id}`} className='sr-only'>
                          Visit note for {s.name}
                        </label>
                        <textarea
                          id={`visit-note-${s.id}`}
                          value={visitDraft}
                          onChange={(e) => setVisitDraft(e.target.value)}
                          rows={3}
                          className='w-full px-3 py-2 border-2 border-current bg-transparent text-sm resize-y min-h-[4rem]'
                          placeholder='What did you see? Stock, empty shelves, what staff said…'
                        />
                        <div className='flex flex-wrap gap-2'>
                          <button
                            type='button'
                            onClick={() => submitVisitLog(s.id)}
                            className='px-3 py-1 text-sm border-2 border-current'
                          >
                            Save visit
                          </button>
                          <button
                            type='button'
                            onClick={cancelVisitLog}
                            className='px-3 py-1 text-sm border-2 border-current opacity-90'
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : editingVisit?.storeId === s.id ? (
                      <p className='text-sm opacity-80'>
                        Finish editing the visit in history below, or cancel there.
                      </p>
                    ) : (
                      <button
                        type='button'
                        onClick={() => openVisitLog(s.id)}
                        className='px-3 py-1 text-sm border-2 border-current'
                      >
                        Log visit
                      </button>
                    )}
                    {s.visitLog.length > 0 ? (
                      <details className='mt-3'>
                        <summary className='text-sm cursor-pointer opacity-90'>
                          Visit history ({s.visitLog.length})
                        </summary>
                        <ul className='mt-3 space-y-3 text-sm pl-1'>
                          {s.visitLog.map((v) => {
                            const isEditingVisit =
                              editingVisit?.storeId === s.id &&
                              editingVisit?.entryId === v.id;
                            return (
                              <li
                                key={v.id}
                                className='border-l-2 border-current pl-3 opacity-95'
                              >
                                {isEditingVisit ? (
                                  <div className='space-y-2 pt-1'>
                                    <div>
                                      <label
                                        htmlFor={`visit-at-${s.id}-${v.id}`}
                                        className='text-xs opacity-80 block mb-1'
                                      >
                                        Visit time
                                      </label>
                                      <input
                                        id={`visit-at-${s.id}-${v.id}`}
                                        type='datetime-local'
                                        value={editVisitAtLocal}
                                        onChange={(e) =>
                                          setEditVisitAtLocal(e.target.value)
                                        }
                                        className='w-full max-w-[min(100%,18.5rem)] px-2 py-1 border-2 border-current bg-transparent'
                                      />
                                    </div>
                                    <div>
                                      <label
                                        htmlFor={`visit-note-edit-${s.id}-${v.id}`}
                                        className='text-xs opacity-80 block mb-1'
                                      >
                                        Note
                                      </label>
                                      <textarea
                                        id={`visit-note-edit-${s.id}-${v.id}`}
                                        value={editVisitNote}
                                        onChange={(e) =>
                                          setEditVisitNote(e.target.value)
                                        }
                                        rows={3}
                                        className='w-full px-3 py-2 border-2 border-current bg-transparent resize-y min-h-[4rem]'
                                      />
                                    </div>
                                    <div className='flex flex-wrap gap-2'>
                                      <button
                                        type='button'
                                        onClick={() => saveEditVisit(s.id)}
                                        className='px-3 py-1 text-sm border-2 border-current'
                                      >
                                        Save
                                      </button>
                                      <button
                                        type='button'
                                        onClick={cancelEditVisit}
                                        className='px-3 py-1 text-sm border-2 border-current opacity-90'
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <time
                                      className='text-xs opacity-80 block'
                                      dateTime={v.at}
                                    >
                                      {formatVisitAt(v.at)}
                                    </time>
                                    <p className='whitespace-pre-wrap mt-1'>
                                      {v.note}
                                    </p>
                                    <button
                                      type='button'
                                      onClick={() => openEditVisit(s.id, v)}
                                      className='mt-2 px-3 py-1 text-xs border-2 border-current opacity-90'
                                    >
                                      Edit visit
                                    </button>
                                  </>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </details>
                    ) : visitLoggingForId !== s.id ? (
                      <p className='text-sm opacity-70 mt-2'>No visits logged yet.</p>
                    ) : null}
                  </div>
                </div>
                <div className='flex flex-row sm:flex-col gap-2 shrink-0'>
                  <button
                    type='button'
                    onClick={() => startEdit(s)}
                    className='px-3 py-1 text-sm border-2 border-current'
                  >
                    Edit
                  </button>
                  <button
                    type='button'
                    onClick={() => removeStore(s.id, s.name)}
                    className='px-3 py-1 text-sm border-2 border-current opacity-80'
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
